import { FC } from 'react';

import { ExclamationIcon } from '@heroicons/react/outline';

interface ReshareWarningModalProps {
  open: boolean;
  /** 경고를 확인하고 그래도 다시 전달 */
  onConfirm: () => void;
  /** 취소 (X / 취소 / 백드롭) */
  onClose: () => void;
}

/**
 * 이미 카카오톡으로 전달한 기록이 있는 쿠폰을 다시 전달하려 할 때 띄우는 강한 경고 모달.
 *
 * 같은 쿠폰을 중복 발송하면 먼저/나중에 받은 분 중 한 분만 사용할 수 있고, 다른 분은
 * 사용하지 못하게 된다. 이 경우 발생하는 문제에 대해 책임지지 않음을 명시한 뒤,
 * 그래도 진행할지 확인을 받는다.
 */
export const ReshareWarningModal: FC<ReshareWarningModalProps> = ({
  open,
  onConfirm,
  onClose,
}) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-5 py-3.5">
          <ExclamationIcon className="h-5 w-5 shrink-0 text-red-600" />
          <h3 className="text-base font-bold text-red-700">이미 전달한 쿠폰이에요</h3>
        </div>

        <div className="px-5 pb-5 pt-4">
          {/* 안내 본문 */}
          <p className="mb-3 break-keep text-sm leading-6 text-gray-700">
            이 쿠폰은{' '}
            <span className="font-bold text-red-600">카카오톡으로 이미 전달한 기록</span>
            이 있습니다. 다시 전달하면{' '}
            <span className="inline-block whitespace-nowrap font-bold text-red-600">
              같은 쿠폰이 중복 발송
            </span>
            됩니다.
          </p>

          {/* 핵심 경고 */}
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-3">
            <p className="break-keep text-sm font-bold leading-6 text-red-700">
              쿠폰은 <span className="whitespace-nowrap">한 분만 사용</span>하실 수 있어요.
            </p>
            <p className="mt-1 break-keep text-xs leading-5 text-red-600">
              중복으로 발송하시면 먼저 받으신 분과 나중에 받으신 분 중{' '}
              <span className="font-bold">한 분만 사용 가능</span>하며, 다른 분이
              사용하지 못하게 되는 것에 대해{' '}
              <span className="font-bold">저희가 책임지지 않습니다.</span>
            </p>
          </div>

          {/* 버튼 */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 active:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-bold text-white active:bg-red-700"
            >
              그래도 다시 전달
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
